import { Avatar as AntdAvatar } from "antd";
import React from "react";

const sizeMap = {
  sm: 24,
  md: 36,
  lg: 48,
};

// All colors from design tokens 300 - 600 range
const bgColors = [
  // amber
  "#f9ba54",
  "#f8a729",
  "#ea8f0b",
  "#b9720a",
  // sky
  "#94c1d6",
  "#72acc8",
  "#5ea4c3",
  "#367c9d",
  // rose
  "#d29692",
  "#cb827d",
  "#b4534d",
  "#923c37",
  // olive
  "#d6dfb8",
  "#c2ce96",
  "#abbd72",
  "#809245",
  // gray
  "#e6e5e5",
  "#dedede",
  "#b8b5b4",
  "#a2a09f",
];

export interface AvatarProps {
  name?: string;
  size?: "sm" | "md" | "lg";
  src?: string;
  className?: string;
}

export const Avatar: React.FC<AvatarProps> = ({
  name,
  src,
  size = "md",
  className = "",
}) => {
  const initials = getInitials(name);
  const backgroundColor = getBackgroundColor(name);

  return (
    <AntdAvatar
      src={src}
      size={sizeMap[size]}
      shape="circle"
      style={{ backgroundColor, fontFamily: "var(--indigo-font-body)" }}
      className={`indigo-avatar ${className}`}
    >
      {initials}
    </AntdAvatar>
  );
};

// ******************************
// * Helper functions
// ******************************

// Helper function to extract initials from a name
function getInitials(name?: string): string {
  if (!name) return "";

  const words = name.trim().split(/\s+/);

  if (words.length === 1) {
    // Single name: return first letter
    return words[0][0]?.toUpperCase() || "";
  } else {
    // Multiple names: return first letter of first name + first letter of last name
    const firstInitial = words[0][0]?.toUpperCase() || "";
    const lastInitial = words[words.length - 1][0]?.toUpperCase() || "";
    return firstInitial + lastInitial;
  }
}

// Simple hash function to generate deterministic index from string
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash | 0; // Convert to 32-bit integer
  }
  return Math.abs(hash);
}

// Get deterministic background color based on name
function getBackgroundColor(name?: string): string {
  // Default to gray color when name is empty or undefined
  if (!name || !name.trim()) {
    return "#e6e5e5";
  }

  const hash = hashString(name.toLowerCase().trim());
  const colorIndex = hash % bgColors.length;
  return bgColors[colorIndex];
}
